<template>
    <div class="layout">
        <div class="layout--column column--left">
            <div class="column--left--content">
                <p
                    class="column--left--content--title column--left--content--desktop"
                >
                    <span class="bold-text">Meet the future of CX</span>
                </p>
                <p
                    class="column--left--content--description column--left--content--desktop"
                >
                    The new Transform plan helps your team <br />act on
                    feedback, improve every day and <br />
                    hit your CX goals faster.
                </p>

                <p
                    class="column--left--content--title column--left--content--mobile"
                >
                    <span class="bold-text">Meet the future of CX</span>
                </p>
                <p
                    class="column--left--content--description column--left--content--mobile"
                >
                    The new Transform plan helps your team <br />act on
                    feedback, improve every day and <br />
                    hit your CX goals faster.
                </p>
                <a
                    href="https://www.asknicely.com/product-tour?utm_source=product&utm_medium=login&utm_campaign=project-copilot"
                    target="_blank"
                    class="column--left--content--button"
                    >See it in action</a>
            </div>
        </div>
        <div class="layout--column column--right login">
            <div class="layout--content login-form" @keyup.enter="login">
                <div v-show="error" class="error">{{ error }}</div>
                <CredentialsForm
                    :logo="loginData.logo"
                    class="login-form--credentials-form"
                >
                    <template v-if="mfaEnforce">
                        <div v-if="mfaSuccess">
                            <MFASetupOk
                                :is-loading="isLoading"
                                :codes="recoveryCodes"
                                :from-mfa-setup="true"
                                @onDone="onDone"
                                @onDownload="onDownloadRecoveryCodes"
                            />
                        </div>
                        <div v-else>
                            <MFASetup
                                :email="email"
                                :password="password"
                                :qr-url="mfaQrCode"
                                :mfa-secret="mfaSecret"
                                @mfaUpdate="mfaValidateLogin"
                                @mfaCancel="mfaCancel"
                            />
                        </div>
                    </template>
                    <template v-else-if="!mfaLogin">
                        <template v-if="loginData.emailLoginEnabled">
                            <div class="heading-container">
                                <div class="heading">Sign In</div>
                                <div
                                    v-if="loginData.canSelfRegister"
                                    class="button"
                                >
                                    <a href="/register">
                                        <BaseButton white bordered>Register</BaseButton>
                                    </a>
                                </div>
                            </div>
                            <Input
                                ref="emailRef"
                                v-model="email"
                                title="Email"
                                placeholder="Email"
                                :regex="/.+@.+\..+/"
                                :required="true"
                                err-msg="Valid email is required"
                            />
                            <Input
                                ref="passwordRef"
                                v-model="password"
                                title="Password"
                                placeholder="Password"
                                type="password"
                                :required="true"
                                err-msg="Invalid Password"
                            />
                            <GrecaptchaBox
                                v-if="loginData.hasRecaptcha"
                                ref="grecaptchaRef"
                                :sitekey="loginData.recaptcha"
                            />
                            <BaseButton
                                class="sign-in-button sign-in-button--purple-bg"
                                new-primary
                                full
                                large
                                :click="login"
                                >Sign In</BaseButton>
                            <div class="forgot">
                                <a href="/password">Forgot Password</a>
                            </div>
                        </template>

                        <template
                            v-if="
                                loginData.emailLoginEnabled &&
                                loginData.hasOauthLogins
                            "
                        >
                            <div class="or">
                                <div class="line"></div>
                                <div class="or-text">or</div>
                                <div class="line"></div>
                            </div>
                        </template>

                        <BaseButton
                            v-if="loginData.googleUrl"
                            :click="() => redirectTo(loginData.googleUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/google-oauth-icon.svg"
                            />
                            Sign in with Google
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.oktaUrl"
                            :click="() => redirectTo(loginData.oktaUrl)"
                            class="sign-in-button sign-in-button"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/okta-oauth-icon.png"
                            />
                            Sign in with Okta
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.microsoftUrl"
                            :click="() => redirectTo(loginData.microsoftUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                        >
                            <img
                                class="oauth-icon"
                                src="/img/login/microsoft-oauth-icon.png"
                            />
                            Sign in with Microsoft 365
                        </BaseButton>
                        <BaseButton
                            v-if="loginData.samlSsoUrl"
                            :click="() => redirectTo(loginData.samlSsoUrl)"
                            class="sign-in-button sign-in-button--outline"
                            primary
                            outline
                            full
                            large
                            >{{ loginData.samlSsoName }}</BaseButton>
                    </template>
                    <template v-else>
                        <!-- If user hs no more recovery codes, show new recovery codes if they were generated -->
                        <template v-if="mfaSuccess && recoveryCodes">
                            <MFASetupOk
                                :is-loading="isLoading"
                                :codes="recoveryCodes"
                                :from-mfa-setup="false"
                                @onDone="onDone"
                                @onDownload="onDownloadRecoveryCodes"
                            />
                        </template>
                        <template v-else-if="isMfaRecoveryPage">
                            <MFAVerifyRecoveryCode
                                @onBack="mfaCancel"
                                @onSubmit="onVerifyRecoveryCode"
                            />
                        </template>
                        <template v-else>
                            <div class="heading">Enter Multi-Factor Code</div>
                            <Input
                                ref="mfaRef"
                                v-model="mfa"
                                title="Multi-Factor Authentication Code"
                                type="number"
                                placeholder="6 digit MFA code"
                                :required="true"
                                :err-msg="'Code cannot be empty'"
                            />
                            <Checkbox
                                class="remember-device-checkbox"
                                :checked="mfaRememberDevice"
                                @change="onChangeRememberDevice"
                                >Remember this device for 30 days</Checkbox>
                            <BaseButton
                                class="sign-in-button"
                                new-primary
                                full
                                large
                                :click="login"
                                >Sign In</BaseButton>
                            <div class="forgot">
                                <a href="/password">Forgot Password</a>
                            </div>
                            <div class="lost-access-legend">
                                <a @click="onLostAuthenticatorLink">Lost Access To My Authenticator</a>
                            </div>
                        </template>
                    </template>

                    <div class="ask-logo">
                        <a href="https://www.asknicely.com" target="_blank">
                            <img
                                src="/img/asknicely_logo_grey.svg"
                                alt="AskNicely"
                            />
                        </a>
                    </div>
                </CredentialsForm>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, nextTick } from 'vue'
import CredentialsForm from '@/components/CredentialsForm.vue'
import Input from '@/components/Input.vue'
import BaseButton from '@/components/BaseButton.vue'
import { LoginData } from '@/pages/login/entities/login'
import { loginUser } from '@/api/login'
import GrecaptchaBox from '@/components/GrecaptchaBox.vue'
import Checkbox from '@/components/Rain/Inputs/Checkbox.vue'
import MFASetup from '@/components/Settings/MFASetup.vue'
import MFASetupOk from '@/pages/login/MFASetupOk.vue'
import MFAVerifyRecoveryCode from '@/pages/login/MFAVerifyRecoveryCode.vue'

const DISABLED_SUBDOMAINS: string[] = ['asknicely']

const email = ref('')
const password = ref('')
const mfa = ref('')
const mfaRememberDevice = ref(false)
const loginData = ref<LoginData | undefined>()
const error = ref('')
const mfaLogin = ref(false)
const mfaEnforce = ref(false)
const mfaQrCode = ref('')
const mfaSecret = ref('')
const mfaSuccess = ref(false)
const isMfaRecoveryPage = ref(false)
const recoveryCodes = ref<string[]>([])
const recoveryCode = ref('')
const isLoading = ref(true)
const redirectUrl = ref('')

const emailRef = ref<typeof Input | null>(null)
const passwordRef = ref<typeof Input | null>(null)
const mfaRef = ref<HTMLInputElement | null>(null)
const grecaptchaRef = ref<typeof GrecaptchaBox | null>(null)

onBeforeMount(async () => {
    const dataEl = document.getElementById('login-data')
    const dataJson = dataEl && dataEl.getAttribute('data-json')
    const data: LoginData = dataJson && JSON.parse(dataJson)

    loginData.value = data
    error.value = data.error
})

const login = async () => {
    error.value = ''
    let recaptchaResponse = ''

    if (loginData.value?.hasRecaptcha) {
        recaptchaResponse = grecaptchaRef.value?.getResponse() || ''
        if (recaptchaResponse.length < 1) {
            error.value = 'Please complete reCAPTCHA challenge'
            return
        }
    }

    if (loginData.value?.redirect) {
        redirectUrl.value = loginData.value.redirect
    }

    if (!mfaLogin.value && !mfaEnforce.value) {
        emailRef.value!.hasFocused = true
        passwordRef.value!.hasFocused = true
    }

    if (password.value === '' || !email.value.match(/.+@.+\..+/)) {
        return
    }

    if (mfaLogin.value && mfa.value === '') {
        return
    }

    try {
        const { data } = await loginUser(
            email.value,
            password.value,
            loginData.value?.redirect ? redirectUrl.value : undefined,
            mfaLogin.value || mfaEnforce.value ? mfa.value : undefined,
            loginData.value?.hasRecaptcha ? recaptchaResponse : undefined,
            mfaLogin.value ? mfaRememberDevice.value : undefined,
            recoveryCode.value
        )
        if (data.mfa) {
            mfaLogin.value = true
            nextTick(() => {
                mfaRef.value?.focus()
            })
        } else if (data.mfaEnforce) {
            mfaEnforce.value = true
            mfaQrCode.value = data.codeUrl
            mfaSecret.value = data.mfaSecret
        } else if (data.recoveryCodes) {
            recoveryCodes.value = data.recoveryCodes
            mfaSuccess.value = true
            isLoading.value = false
            redirectUrl.value = data.redirect
        } else {
            redirectTo(data.redirect)
        }
    } catch (e: any) {
        error.value = e.response.data.error
    }
}

const verifyRecoveryCode = async () => {
    try {
        error.value = ''
        let recaptchaResponse = ''

        const { data } = await loginUser(
            email.value,
            password.value,
            mfaLogin.value || mfaEnforce.value ? mfa.value : undefined,
            loginData.value?.hasRecaptcha ? recaptchaResponse : undefined,
            mfaLogin.value ? mfaRememberDevice.value : undefined,
            recoveryCode.value
        )

        if (data.recoveryCodes) {
            recoveryCodes.value = data.recoveryCodes
            mfaSuccess.value = true
            isLoading.value = false
            redirectUrl.value = data.redirect
        } else {
            redirectTo(data.redirect)
        }
    } catch (e: any) {
        error.value = e.response.data.error
    }
}

const mfaValidateLogin = (mfaCode: string) => {
    if (!mfaCode || mfaCode.length !== 6) {
        error.value = 'Please enter your 6 digit MFA code'
        return
    }
    mfa.value = mfaCode
    login()
}

const onDownloadRecoveryCodes = () => {
    const dataAsString = recoveryCodes.value.join('\n')
    const blob = new Blob([dataAsString], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'recovery-codes.txt'
    document.body.appendChild(link)

    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
}

onMounted(() => {
    window.addEventListener('keydown', (e) => {
        const isCommandG = (e.metaKey || e.ctrlKey) && e.key === 'g'
        const subDomain = window.location.host.split('.')[0]
        const notDisabled = DISABLED_SUBDOMAINS.indexOf(subDomain) === -1
        if (notDisabled && isCommandG && loginData.value?.enabledGLogin) {
            e.preventDefault()
            redirectTo(loginData.value.asknicelyLoginUrl)
        }
    })
})

const mfaCancel = () => {
    error.value = ''
    mfaEnforce.value = false
    mfaQrCode.value = ''
    mfaSecret.value = ''
    isMfaRecoveryPage.value = false
    recoveryCode.value = ''
}

const redirectTo = (link: string) => {
    return location.replace(link)
}

const onChangeRememberDevice = (value: boolean): void => {
    mfaRememberDevice.value = value
}

const onLostAuthenticatorLink = (): void => {
    isMfaRecoveryPage.value = true
}

const onVerifyRecoveryCode = (code: string) => {
    if (!code) {
        error.value = 'Recovery code required.'
        return
    }
    recoveryCode.value = code
    verifyRecoveryCode()
}

const onDone = () => {
    redirectTo(redirectUrl.value)
}
</script>

<style lang="less" scoped>
@import '../../styles/palette';
@import '@/styles/prime-vue/variables.less';

/* Layout */
.layout {
    display: flex;
    height: 100%;
    width: 100%;
    background-color: #f1eee9;

    &--column {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

.column {
    &--left {
        align-items: start;
        color: @white;
        // Desktop background (image)

        background: url('~@/assets/img/takeover/bg-desktop.png'),
            linear-gradient(
                151deg,
                var(--Royalpurple, #662280) 4.19%,
                #290737 70.71%
            );
        background-position: bottom, center;
        background-size: contain, cover;
        background-repeat: no-repeat, no-repeat;

        max-width: 1000px;

        &--content {
            width: auto;
            margin-top: 10%;

            &--title {
                display: flex;
                font-size: 36px;
                font-weight: 600;
                line-height: 42.5px;
                font-family: Montserrat;
                align-items: center;
                margin: 0;

                .bold-text {
                    font-weight: 900;
                }
            }

            &--image img {
                width: 500px;
                border-radius: @radius-lg;
                margin-top: 10px;
            }

            &--desktop {
                display: block;
            }

            &--mobile {
                display: none;
            }

            &--description {
                font-size: 15.65px;
                font-weight: 400;
                line-height: 25px;
                font-family: Montserrat;
                text-align: left;
                max-width: 450px;
            }

            &--button {
                display: inline-block;
                color: white;
                background-color: #02c181;
                font-size: 16px;
                font-weight: 700;
                font-family: Montserrat;
                line-height: 17.6px;
                border-radius: 32.6px;
                padding: 10px 30px;
                margin-top: @spacing-xs;

                &:hover {
                    text-decoration: none;
                    background-color: #04d996;
                }
            }
        }
    }

    &--right {
        background-color: @white;
    }
}

/* Media query for tablets in portrait mode (covers iPad, Android tablets, Surface, etc.) */
@media screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    .column {
        &--left {
            &--content {
                margin-top: 12%;
                margin-left: 5%;
                width: 90%;
                max-width: 500px;

                &--title {
                    font-size: 32px;
                    line-height: 38px;
                    margin-bottom: 20px;
                }

                &--description {
                    font-size: 16px;
                    line-height: 24px;
                    max-width: 420px;
                    margin-bottom: 25px;
                }

                &--button {
                    font-size: 15px;
                    padding: 12px 28px;
                }
            }
        }
    }

    .login {
        .login-form {
            &--credentials-form {
                width: 380px;
            }
        }
    }
}

/* Media query for small tablets in portrait mode (7-8 inch tablets) */
@media screen and (min-width: 768px) and (max-width: 900px) and (orientation: portrait) {
    .column {
        &--left {
            &--content {
                width: 85%;
                margin-top: 15%;
                margin-left: 6%;

                &--title {
                    font-size: 28px;
                    line-height: 36px;
                }

                &--description {
                    font-size: 15px;
                    line-height: 23px;
                    max-width: 380px;
                }
            }
        }
    }
}

/* Media query for tablets in landscape mode */
@media screen and (min-width: 1024px) and (max-width: 1366px) and (orientation: landscape) {
    .column {
        &--left {
            &--content {
                margin-top: 8%;
                margin-left: 8%;
                max-width: 480px;

                &--title {
                    font-size: 30px;
                    line-height: 36px;
                    margin-bottom: 18px;
                }

                &--description {
                    font-size: 15px;
                    line-height: 23px;
                    max-width: 420px;
                    margin-bottom: 22px;
                }

                &--button {
                    font-size: 15px;
                    padding: 11px 26px;
                }
            }
        }
    }

    .login {
        .login-form {
            &--credentials-form {
                width: 360px;
            }
        }
    }
}

/* Media query for screens smaller than 768px */
@media screen and (max-width: 767px) {
    .layout {
        flex-direction: column;

        &--column {
            flex: none;
            padding: @spacing-6xl 0;
        }
    }

    .column {
        &--left {
            color: @white;
            // Mobile background (gradient) - override the desktop image
            background: url('~@/assets/img/takeover/bg-mobile.png'),
                linear-gradient(
                    151deg,
                    var(--Royalpurple, #662280) 4.19%,
                    #290737 70.71%
                );
            background-position:
                bottom right,
                center;
            background-size:
                100px 122px,
                cover;
            background-repeat: no-repeat, no-repeat;

            &--content {
                margin: 0;
                width: 350px;

                &--title {
                    display: flex;
                    font-size: 22px;
                    font-weight: 500;
                    line-height: 28px;
                    align-items: center;
                    margin-bottom: 15px;
                }

                &--description {
                    font-size: 14px;
                }

                &--button {
                    padding: 10px 20px;
                    font-size: 14px;
                }

                &--image {
                    display: none;
                }

                &--desktop {
                    display: none;
                }

                &--mobile {
                    display: block;
                }
            }
        }
    }
}

.login {
    .login-form {
        margin: 0 auto;

        &--credentials-form {
            padding: 0 !important;
            width: 350px;
        }

        .input-container {
            :deep(::-webkit-outer-spin-button),
            :deep(::-webkit-inner-spin-button) {
                display: none;
                -webkit-appearance: none;
                margin: 0;
            }

            :deep(input[type='number']) {
                -moz-appearance: textfield; /* Firefox */
            }
        }

        .remember-device-checkbox {
            margin-top: 14px;
        }

        .sign-in-button {
            margin-top: 14px;

            &--purple-bg {
                color: @white;
                background-color: @bgDarkGradientStart;
            }

            &--outline {
                border-color: #d0d5dd;
                color: #344054;
            }
        }

        .ask-logo {
            margin-top: 22px;
            text-align: center;

            img {
                height: 20px;
            }
        }

        .heading {
            margin-top: 22px;
            font-size: 20px;
            font-weight: 500;
        }

        .heading-container {
            display: flex;
            align-items: center;

            .heading {
                flex: 1;
                margin-top: 0;
            }
        }

        .error {
            color: @red;
            font-size: 12px;
            margin-top: 8px;
            background-color: #ffebeb;
            padding: 12px 10px;
            text-align: center;
            border-radius: 5px;
            margin-bottom: 20px;
        }

        .forgot,
        .lost-access-legend {
            text-align: right;
            margin-top: 10px;

            a {
                font-size: 13px;
                color: @grey40;
                text-decoration: none;
            }
        }

        .or {
            display: flex;
            text-align: center;
            color: @grey40;
            padding-top: 14px;

            .line {
                width: 160px;
                border-bottom: 1px solid @grey40;
                position: relative;
                top: -8px;
            }

            .or-text {
                padding: 0 8px;
            }
        }

        .oauth-icon {
            position: relative;
            top: 3px;
            left: -2px;
        }
    }
}
.lost-access-legend {
    line-height: 18px;
    cursor: pointer;
}

@font-face {
    font-family: 'Montserrat';
    src: url('~@/assets/fonts/Montserrat/Montserrat-Medium.ttf')
        format('truetype');
    font-weight: 600;
}

@font-face {
    font-family: 'Montserrat';
    src: url('~@/assets/fonts/Montserrat/Montserrat-Light.ttf')
        format('truetype');
    font-weight: 400;
}

@font-face {
    font-family: 'Montserrat';
    src: url('~@/assets/fonts/Montserrat/Montserrat-Bold.ttf')
        format('truetype');
    font-weight: 700;
}

@font-face {
    font-family: 'Montserrat';
    src: url('~@/assets/fonts/Montserrat/Montserrat-ExtraBold.ttf')
        format('truetype');
    font-weight: 900;
}
</style>
